import React, { useState, useEffect } from 'react';
import { DatabaseService } from '../db/database';
import apiService from '../services/apiService';
import holidayService from '../services/holidayService';
import './Settings.css';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
];

function Settings({ onClose, onSave }) {
  const [userInfo, setUserInfo] = useState({ name: '', email: '', countryCode: '' });
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
    loadCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const info = await DatabaseService.getUserInfo();
      setUserInfo(info);

      const days = await DatabaseService.getWorkingDays();
      setWorkingDays(days);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      // Try to fetch from client-side holiday service first
      const countriesList = await holidayService.getAvailableCountries();
      setCountries(countriesList);
    } catch (error) {
      console.warn('Could not load countries list, using defaults');
      const defaultCountries = holidayService.getDefaultCountries();
      setCountries(defaultCountries);
    }
  };

  const toggleWorkingDay = (dayValue) => {
    setWorkingDays(prev => {
      if (prev.includes(dayValue)) {
        // Don't allow removing all days
        if (prev.length === 1) {
          setMessage('You must have at least one working day');
          setTimeout(() => setMessage(''), 3000);
          return prev;
        }
        return prev.filter(d => d !== dayValue);
      } else {
        return [...prev, dayValue].sort((a, b) => a - b);
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');

      const originalCountry = await DatabaseService.getSetting('countryCode');
      const countryChanged = originalCountry !== userInfo.countryCode;

      // Save working days
      await DatabaseService.setWorkingDays(workingDays);

      // Save user info if changed
      await DatabaseService.setUserInfo(userInfo.name, userInfo.email, userInfo.countryCode);

      // If country changed, refresh holidays from client-side API
      if (countryChanged) {
        try {
          setMessage('Updating holidays for new country...');
          const currentYear = new Date().getFullYear();
          const holidays = await holidayService.getPublicHolidaysRange(
            userInfo.countryCode,
            currentYear,
            currentYear + 1
          );

          if (holidays && holidays.length > 0) {
            // Clear old holidays and save new ones
            await DatabaseService.clearHolidays();
            await DatabaseService.setHolidays(holidays);
            console.log(`Updated holidays for ${userInfo.countryCode}: ${holidays.length} holidays loaded`);
          }
        } catch (holidayError) {
          console.warn('Failed to update holidays:', holidayError);
          setMessage('Settings saved, but could not update holidays. Try syncing later.');
        }
      }

      // Try to update on server (optional)
      try {
        await apiService.updateUserCountry(userInfo.email, userInfo.countryCode);
      } catch (apiError) {
        console.warn('Could not update server:', apiError);
      }

      setMessage('Settings saved successfully!');
      setTimeout(() => {
        if (onSave) onSave();
        if (onClose) onClose();
      }, 1500);
    } catch (error) {
      setMessage('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-modal">
        <div className="settings-content">
          <div className="loading">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-modal" onClick={onClose}>
      <div className="settings-content" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {message && (
          <div className={`settings-message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="settings-section">
          <h3>User Information</h3>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={userInfo.name}
              onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={userInfo.email}
              onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Country (for bank holidays)</label>
            <select
              value={userInfo.countryCode}
              onChange={(e) => setUserInfo({ ...userInfo, countryCode: e.target.value })}
            >
              {countries.map(country => (
                <option key={country.countryCode} value={country.countryCode}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="settings-section">
          <h3>Working Days</h3>
          <p className="help-text">
            Select which days of the week you normally work. Statistics will only count these days.
          </p>
          <div className="working-days-grid">
            {DAYS_OF_WEEK.map(day => (
              <button
                key={day.value}
                className={`day-btn ${workingDays.includes(day.value) ? 'active' : ''}`}
                onClick={() => toggleWorkingDay(day.value)}
              >
                <span className="day-short">{day.short}</span>
                <span className="day-label">{day.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="settings-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
